import React, {forwardRef, PropsWithChildren} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {ChromelessButton} from '../../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../../../../libs/shared/ui/src/components/icon/icon';
import {VisuallyHidden} from '../../../../../../../libs/shared/ui/src/components/visuallyHidden/visuallyHidden';

/*
 * Props.
 */

export interface MessageBannerActionGroupProps {
  onForcePopout?: () => void;
  onRequestClose?: () => void;
  color?: string;
  useDarkHover?: boolean;
}

/*
 * Style.
 */

const StyledActionButtonGroup = styled.div`
  grid-area: action;
  display: flex;
`;

const StyledNavigationButton = styled(ChromelessButton)<{$color?: string; $useDarkHover?: boolean}>`
  padding: 3px;
  border-radius: 13px;
  color: ${(p) => p.$color || p.theme.greys.white};
  margin-left: 6px;

  &:hover {
    background-color: ${(p) => (p.$useDarkHover ? p.theme.alphas.gray20 : p.theme.alphas.white30)};
  }
`;

/*
 * Component.
 */

export const MessageBannerActionGroup = forwardRef<
  HTMLDivElement,
  PropsWithChildren<MessageBannerActionGroupProps>
>((props, ref) => (
  <StyledActionButtonGroup ref={ref}>
    {props.children}
    {maybeRenderNavigationItems(props)}
  </StyledActionButtonGroup>
));

/*
 * Helpers.
 */

function maybeRenderNavigationItems(props: MessageBannerActionGroupProps) {
  const {onForcePopout, onRequestClose} = props;

  return (
    <>
      {onForcePopout && (
        <StyledNavigationButton
          onClick={onForcePopout}
          $color={props.color}
          $useDarkHover={props.useDarkHover}
        >
          <Icon name="popout" />
          <VisuallyHidden>
            <Trans id="dialogheader-popout">Popout</Trans>
          </VisuallyHidden>
        </StyledNavigationButton>
      )}
      {onRequestClose && (
        <StyledNavigationButton
          onClick={onRequestClose}
          $color={props.color}
          $useDarkHover={props.useDarkHover}
        >
          <Icon name="close" />
          <VisuallyHidden>
            <Trans id="dialogheader-close">Close</Trans>
          </VisuallyHidden>
        </StyledNavigationButton>
      )}
    </>
  );
}
